/**
 * Shared overflow / context-menu chrome.
 *
 * **SoT (visual):** Documents panel `...` and document viewer `...`
 * (`document-list-toolbar`, `document-viewer`) - soft border, quiet Ink,
 * optional uppercase section labels, destructive quiet at rest.
 * **Gallery:** `/?test=maater-ui#menu-chrome`
 *
 * Tall menus must stay inside the viewport when the window is short (DevTools
 * open, laptop). max-height + overflow-y-auto is part of the chrome contract -
 * do not remove without an alternate collision story.
 */

/** Shared row / border / scroll tokens (without fixed width). */
const QUIET_OVERFLOW_MENU_SURFACE =
  'max-h-[min(70vh,calc(100dvh-1.5rem))] overflow-y-auto overscroll-contain border-border/60 shadow-sm p-1.5 [&_[role=menuitem]]:px-3 [&_[role=menuitem]]:py-2 [&_[role=menuitem]]:gap-2.5 [&_[role=menuitem]_svg]:mr-0'

/**
 * Standard overflow shell - fixed `w-60` like document viewer.
 * Use for Documents / viewer / chat `...` and any multi-section menu.
 */
export const QUIET_OVERFLOW_MENU_CONTENT_CLASS = `w-60 ${QUIET_OVERFLOW_MENU_SURFACE}`

/**
 * Compact shell - hug content (header Sign out, 1-3 item menus).
 * Same gloss as standard; do not use fixed `w-60` (looks empty / left-justified).
 */
export const QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS = `w-auto min-w-[9rem] ${QUIET_OVERFLOW_MENU_SURFACE}`

/**
 * Floating pickers (calendar, due date, compact popovers).
 * Same gloss as overflow - soft border, quiet shadow. Never stock
 * `border` + `shadow-md` (reads as a hard white card in dark).
 */
export const QUIET_POPOVER_CONTENT_CLASS =
  'z-50 w-auto rounded-md border border-border/60 bg-popover p-0 text-popover-foreground shadow-sm outline-none'

/**
 * Tooltip surface (`@/components/ui/tooltip`). Same gloss as popovers - soft
 * border, quiet shadow. Never stock `border` + `shadow-md` (hard white card in dark).
 */
export const QUIET_TOOLTIP_CONTENT_CLASS =
  'z-50 overflow-hidden rounded-md border border-border/60 bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-sm'

/** Shell for EntityContextMenu (fixed-position browse/folder menus). */
export const QUIET_CONTEXT_MENU_SHELL_CLASS =
  'fixed z-50 w-60 min-w-[200px] max-h-[min(70vh,calc(100dvh-1.5rem))] overflow-y-auto overscroll-contain bg-popover border border-border/60 rounded-md shadow-sm p-1.5'

/** Menu item row padding/gap to match DropdownMenu quiet chrome. */
export const QUIET_CONTEXT_MENU_ITEM_CLASS =
  'w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none'

/** Quiet section label inside overflow menus (scannability without loud chrome). */
export const QUIET_OVERFLOW_MENU_LABEL_CLASS =
  'px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70'

/**
 * Destructive only on hover/focus - never loud at rest.
 * Hover uses solid destructive + foreground (light) text. A 10% wash +
 * `text-destructive` fails in dark mode (token is a dark red on a dark red).
 */
export const QUIET_MENU_DESTRUCTIVE_ITEM_CLASS =
  'rounded-sm hover:!bg-destructive hover:!text-destructive-foreground focus:!bg-destructive focus:!text-destructive-foreground data-[highlighted]:!bg-destructive data-[highlighted]:!text-destructive-foreground [&_svg]:text-current'

/** Prefer this on DropdownMenuContent so Radix keeps the panel in-bounds. */
export const QUIET_OVERFLOW_MENU_COLLISION_PADDING = 12

/**
 * Icon-only delete/remove (Settings rows, token lists).
 * Quiet at rest - same spirit as QUIET_MENU_DESTRUCTIVE_ITEM_CLASS.
 * Gallery reference: #menu-chrome destructive; production Settings use this on trash icons.
 */
export const QUIET_ICON_BUTTON_DESTRUCTIVE_CLASS =
  'text-muted-foreground hover:text-destructive hover:bg-destructive/10'

/**
 * Gallery-only: force a short viewport so agents can see overflow-y scroll
 * without a full-height window. Production menus use the 70vh cap instead.
 */
export const QUIET_OVERFLOW_MENU_GALLERY_SCROLL_DEMO_CLASS = 'max-h-[240px]'
